import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, type Me } from './api';

type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; me: Me };

type AuthContextValue = {
  state: AuthState;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<Me>('/api/auth/me');
      setState({ status: 'signedIn', me });
    } catch (err) {
      // A 401 is the normal signed-out path, not an error worth surfacing.
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'signedOut' });
      } else {
        setState({ status: 'signedOut' });
        console.error('Failed to load session', err);
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.post('/api/auth/logout');
    setState({ status: 'signedOut' });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
